import React, { useState, useEffect } from 'react';
import { Card, Button, Select, InputNumber, Form, Row, Col, Table, Tag, Modal, Input, Tooltip, message } from 'antd';
import { PlusOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EmployeePayments = () => {
  const [payments, setPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filters, setFilters] = useState({ employeeId: '', hostelId: '', month: '', year: new Date().getFullYear(), status: '' });
  const { user } = useAuth();
  const [recordForm] = Form.useForm();
  const [paymentForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [employeesRes, hostelsRes] = await Promise.all([api.get('/admin/team-members'), api.get('/hostels')]);
      setEmployees(employeesRes.data);
      setHostels(hostelsRes.data);
    } catch (error) {
      message.error('Failed to load data');
      console.error(error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.employeeId) params.employeeId = filters.employeeId;
      if (filters.hostelId) params.hostelId = filters.hostelId;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.status) params.status = filters.status;
      const response = await api.get('/employee-payments', { params });
      setPayments(response.data);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = !filters.hostelId
    ? employees.filter((emp) => {
        if (user?.role === 'employee' && user?.employeeType === 'accountant' && emp._id === user._id) return false;
        return true;
      })
    : employees.filter((emp) => {
        if (user?.role === 'employee' && user?.employeeType === 'accountant' && emp._id === user._id) return false;
        if (emp.employeeType === 'accountant') {
          return emp.accessibleHostels?.some((h) => (h._id || h).toString() === filters.hostelId);
        }
        const empHostelId = emp.hostel?._id || emp.hostel;
        return empHostelId && (empHostelId === filters.hostelId || empHostelId.toString() === filters.hostelId);
      });

  const recordFormHostel = Form.useWatch('hostel', recordForm);
  const employeesForRecordModal = !recordFormHostel
    ? filteredEmployees
    : employees.filter((emp) => {
        if (user?.role === 'employee' && user?.employeeType === 'accountant' && emp._id === user._id) return false;
        if (emp.employeeType === 'accountant') {
          return emp.accessibleHostels?.some((h) => (h._id || h).toString() === recordFormHostel);
        }
        const empHostelId = emp.hostel?._id || emp.hostel;
        return empHostelId && (empHostelId === recordFormHostel || empHostelId.toString() === recordFormHostel);
      });

  const canEdit = user?.role === 'owner' || (user?.role === 'employee' && user?.employeeType === 'accountant');
  const canEditPayment = (payment) => {
    if (user?.role === 'owner') return true;
    if (user?.role === 'employee' && user?.employeeType === 'accountant') return payment.employee?._id !== user._id;
    return false;
  };

  const handleAddRecord = () => {
    setSelectedPayment(null);
    recordForm.resetFields();
    setShowRecordModal(true);
  };

  const handleRecordSubmit = async () => {
    try {
      const values = await recordForm.validateFields();
      if (selectedPayment) {
        await api.patch(`/employee-payments/${selectedPayment._id}`, values);
        message.success('Record updated');
      } else {
        await api.post('/employee-payments', values);
        message.success('Record created');
      }
      recordForm.resetFields();
      setShowRecordModal(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleAddPaymentEntry = (payment) => {
    setSelectedPayment(payment);
    paymentForm.setFieldsValue({ paymentDate: new Date().toISOString().split('T')[0], amount: '', description: '' });
    setShowPaymentModal(true);
  };

  const handlePaymentEntrySubmit = async () => {
    try {
      const values = await paymentForm.validateFields();
      const remaining = selectedPayment.monthlySalary - selectedPayment.payments.reduce((s, p) => s + p.amount, 0);
      if (values.amount > remaining) {
        message.error(`Amount cannot exceed remaining: $${remaining.toFixed(2)}`);
        return;
      }
      const response = await api.post(`/employee-payments/${selectedPayment._id}/payments`, values);
      if (response.data?.nextMonthMessage) message.info(response.data.nextMonthMessage);
      message.success('Payment added');
      paymentForm.resetFields();
      setShowPaymentModal(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleDeleteRecord = (payment) => {
    Modal.confirm({
      title: 'Delete this payment record?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/employee-payments/${payment._id}`);
          message.success('Record deleted');
          fetchPayments();
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleDeletePaymentEntry = (payment, entry) => {
    Modal.confirm({
      title: 'Delete this payment entry?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/employee-payments/${payment._id}/payments/${entry._id}`);
          message.success('Entry deleted');
          fetchPayments();
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const params = { ...filters };
      const response = await api.get('/employee-payments/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = 'employee-payments';
      if (filters.month) filename += `-${new Date(2000, filters.month - 1).toLocaleString('default', { month: 'long' })}`;
      if (filters.year) filename += `-${filters.year}`;
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export downloaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Export failed');
    }
  };

  const calculateTotalPaid = (payment) => payment.payments.reduce((sum, p) => sum + p.amount, 0);
  const calculateRemaining = (payment) => payment.monthlySalary - calculateTotalPaid(payment);

  if (loading && payments.length === 0) return null;

  return (
    <>
      <Card
        title={<span style={{ fontSize: 20, fontWeight: 600 }}>Employee Payments</span>}
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export CSV</Button>
            {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRecord}>Create Payment Record</Button>}
          </div>
        }
      >
        <Card title="Filters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <label>Hostel</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Hostels"
                value={filters.hostelId || undefined}
                onChange={(v) => setFilters({ ...filters, hostelId: v || '', employeeId: '' })}
                options={[{ value: '', label: 'All Hostels' }, ...hostels.map((h) => ({ value: h._id, label: h.name }))]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Employee</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder={filters.hostelId ? 'All (in this hostel)' : 'All Employees'}
                value={filters.employeeId || undefined}
                onChange={(v) => setFilters({ ...filters, employeeId: v || '' })}
                disabled={!filters.hostelId}
                options={[{ value: '', label: filters.hostelId ? 'All (in this hostel)' : 'All Employees' }, ...filteredEmployees.map((e) => ({ value: e._id, label: e.name }))]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Month</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Months"
                value={filters.month || undefined}
                onChange={(v) => setFilters({ ...filters, month: v || '' })}
                options={[{ value: '', label: 'All Months' }, ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(2000, i).toLocaleString('default', { month: 'long' }) }))]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Year</label>
              <InputNumber style={{ width: '100%', marginTop: 4 }} min={2020} max={2100} value={filters.year} onChange={(v) => setFilters({ ...filters, year: v || new Date().getFullYear() })} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Status</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Status"
                value={filters.status || undefined}
                onChange={(v) => setFilters({ ...filters, status: v || '' })}
                options={[{ value: '', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'partial', label: 'Partial' }, { value: 'paid', label: 'Paid' }]}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Payment Records">
          {payments.length === 0 ? (
            <p>No payment records found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {payments.map((payment) => {
                const totalPaid = calculateTotalPaid(payment);
                const remaining = calculateRemaining(payment);
                const monthName = new Date(2000, payment.paymentMonth - 1).toLocaleString('default', { month: 'long' });
                return (
                  <Card
                    key={payment._id}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>{payment.employee?.name || 'Unknown'} · {payment.hostel?.name || 'N/A'} · {monthName} {payment.paymentYear}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <Tag color={payment.status === 'paid' ? 'green' : payment.status === 'partial' ? 'orange' : 'default'}>{payment.status}</Tag>
                          {user?.role === 'owner' && canEditPayment(payment) && (
                            <Tooltip title="Delete Record">
                              <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteRecord(payment)} />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    }
                  >
                    <Row gutter={[24, 16]} style={{ marginBottom: 16 }}>
                      <Col><strong>Monthly Salary:</strong> ${payment.monthlySalary.toFixed(2)}</Col>
                      <Col><strong>Total Paid:</strong> ${totalPaid.toFixed(2)}</Col>
                      <Col><strong>Remaining:</strong> <span style={{ color: remaining > 0 ? '#ff4d4f' : '#52c41a' }}>${remaining.toFixed(2)}</span></Col>
                      <Col><strong>Due Date:</strong> {new Date(payment.dueDate).toLocaleDateString()}</Col>
                      {payment.notes && <Col><strong>Notes:</strong> {payment.notes}</Col>}
                    </Row>
                    {payment.payments.length > 0 && (
                      <Table
                        rowKey="_id"
                        size="small"
                        columns={[
                          { title: 'Date', dataIndex: 'paymentDate', render: (d) => new Date(d).toLocaleDateString() },
                          { title: 'Amount', dataIndex: 'amount', render: (v) => `$${v.toFixed(2)}` },
                          { title: 'Description', dataIndex: 'description', render: (t) => t || '-' },
                          { title: 'Recorded By', dataIndex: ['recordedBy', 'name'], render: (t) => t || 'N/A' },
                          ...(canEditPayment(payment) ? [{ title: 'Actions', render: (_, entry) => <Tooltip title="Delete"><Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeletePaymentEntry(payment, entry)} /></Tooltip> }] : []),
                        ]}
                        dataSource={payment.payments}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                      />
                    )}
                    {canEditPayment(payment) && remaining > 0 && (
                      <Button type="primary" style={{ marginTop: 16 }} onClick={() => handleAddPaymentEntry(payment)}>Add Payment</Button>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </Card>

      <Modal
        title={selectedPayment ? 'Edit Payment Record' : 'Create Payment Record'}
        open={showRecordModal}
        onCancel={() => { setShowRecordModal(false); setSelectedPayment(null); recordForm.resetFields(); }}
        onOk={handleRecordSubmit}
        destroyOnClose
        okText={selectedPayment ? 'Update' : 'Create'}
        width={520}
      >
        <Form form={recordForm} layout="vertical" initialValues={{ paymentMonth: new Date().getMonth() + 1, paymentYear: new Date().getFullYear() }}>
          <Form.Item name="hostel" label="Hostel" rules={[{ required: true }]}>
            <Select
              placeholder="Select hostel"
              disabled={!!selectedPayment}
              options={hostels.map((h) => ({ value: h._id, label: h.name }))}
              onChange={() => recordForm.setFieldValue('employee', undefined)}
            />
          </Form.Item>
          <Form.Item name="employee" label="Employee" rules={[{ required: true }]}>
            <Select
              placeholder={recordFormHostel ? 'Select employee (in this hostel)' : 'Select hostel first'}
              disabled={!!selectedPayment || !recordFormHostel}
              options={employeesForRecordModal.map((e) => ({ value: e._id, label: e.name }))}
            />
          </Form.Item>
          <Form.Item name="monthlySalary" label="Monthly Salary" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="paymentMonth" label="Month" rules={[{ required: true }]}>
            <Select disabled={!!selectedPayment} options={Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(2000, i).toLocaleString('default', { month: 'long' }) }))} />
          </Form.Item>
          <Form.Item name="paymentYear" label="Year" rules={[{ required: true }]}>
            <InputNumber min={2020} max={2100} style={{ width: '100%' }} disabled={!!selectedPayment} />
          </Form.Item>
          <Form.Item name="dueDate" label="Due Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Payment"
        open={showPaymentModal}
        onCancel={() => { setShowPaymentModal(false); setSelectedPayment(null); paymentForm.resetFields(); }}
        onOk={handlePaymentEntrySubmit}
        destroyOnClose
        okText="Add Payment"
      >
        {selectedPayment && (
          <>
            <p><strong>Employee:</strong> {selectedPayment.employee?.name}</p>
            <p><strong>Monthly Salary:</strong> ${selectedPayment.monthlySalary.toFixed(2)}</p>
            <p><strong>Remaining:</strong> ${calculateRemaining(selectedPayment).toFixed(2)}</p>
            <Form form={paymentForm} layout="vertical">
              <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                <InputNumber min={0.01} max={calculateRemaining(selectedPayment)} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} placeholder="e.g. Partial payment" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};

export default EmployeePayments;
